import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { suppliesApi } from "../api/supplies";
import apiClient from "@/api/client";

const QK = "supply-items";

export function useSupplyItems(params?: Record<string, string>) {
  return useQuery({
    queryKey: [QK, params],
    queryFn: () => suppliesApi.list(params),
    staleTime: 0,
  });
}

export function useCreateSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: suppliesApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useUpdateSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof suppliesApi.update>[1] }) =>
      suppliesApi.update(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useDeleteSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: suppliesApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useApproveSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => suppliesApi.approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useOrderSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data?: Parameters<typeof suppliesApi.order>[1];
    }) => suppliesApi.order(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useRejectSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { notes?: string } }) =>
      suppliesApi.reject(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useMarkDeliveredSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: { delivered_at?: string } }) =>
      suppliesApi.markDelivered(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useReopenSupplyItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: suppliesApi.reopen,
    onSuccess: () => qc.invalidateQueries({ queryKey: [QK] }),
  });
}

export function useSuppliesPendingCount(enabled = true) {
  return useQuery({
    queryKey: ["supplies-pending-count"],
    queryFn: () =>
      apiClient
        .get<{ data: number }>("/supplies/pending-count/")
        .then((r) => r.data.data),
    enabled,
    staleTime: 30_000,
  });
}
